import React, { useState, useEffect } from 'react';
import { DragDropContext, Draggable } from 'react-beautiful-dnd';
import { StrictModeDroppable } from './StrictModeDroppable';
import { useProject } from '../../context/ProjectContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { supabase } from '../../lib/supabase';
import { PlusIcon, TableCellsIcon, DocumentTextIcon, FolderIcon, FolderOpenIcon, ViewColumnsIcon, ListBulletIcon, ArrowsUpDownIcon, ArrowLeftIcon, EllipsisVerticalIcon, ChatBubbleBottomCenterTextIcon, GlobeAltIcon, TrashIcon, NewspaperIcon } from '@heroicons/react/24/outline';
import CreateDocumentModal from './CreateDocumentModal';
import CreateTableModal from '../tables/CreateTableModal';
import CreateFolderModal from '../folders/CreateFolderModal';
import { useNavigate } from 'react-router-dom';
import MoveFolderModal from './MoveFolderModal';
import SocialNetworkModal from './SocialNetworkModal';
import { useAuth } from '../../context/AuthContext';
import IconComponent from '../icons/IconComponent';
import { useUser } from '../../context/UserContext';


const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

function Documents() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const { addToast } = useToast();
  const { confirm } = useConfirm();
  
  const [loading, setLoading] = useState(true);
  const [tables, setTables] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [showDocumentTypeModal, setShowDocumentTypeModal] = useState(false);
  const [showCreateTableModal, setShowCreateTableModal] = useState(false);
  const [folders, setFolders] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('updated'); // 'updated' or 'name'
  const [folderPath, setFolderPath] = useState([]); // Store the current path
  const [breadcrumbs, setBreadcrumbs] = useState([]); // Store navigation breadcrumbs
  const [menuOpen, setMenuOpen] = useState(null);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSocialNetworkModal, setShowSocialNetworkModal] = useState(false);

  useEffect(() => {
    if (currentProject) {
      loadContent();
      loadFolders();
    }
  }, [currentProject, currentFolder]);

  const loadContent = async () => {
    try {
      let tablesQuery = supabase
        .from('data_tables')
        .select('*')
        .eq('project_id', currentProject.id);

      let docsQuery = supabase
        .from('data_docs')
        .select('*')
        .eq('project_id', currentProject.id);

      let socialQuery = supabase
        .from('content')
        .select('*')
        .eq('project_id', currentProject.id);

      let websiteQuery = supabase
        .from('website_seo_checks')
        .select('*')
        .eq('project_id', currentProject.id);

      // Handle folder filtering
      if (currentFolder) {
        tablesQuery = tablesQuery.eq('folder_id', currentFolder.id);
        docsQuery = docsQuery.eq('folder_id', currentFolder.id);
        socialQuery = socialQuery.eq('folder_id', currentFolder.id).or('folder_id.is.null');
        websiteQuery = websiteQuery.eq('folder_id', currentFolder.id);
      } else {
        tablesQuery = tablesQuery.is('folder_id', null);
        docsQuery = docsQuery.is('folder_id', null);
        socialQuery = socialQuery.is('folder_id', null).or('folder_id.is.null');
        websiteQuery = websiteQuery.is('folder_id', null);
      }

      // Add ordering
      tablesQuery = tablesQuery.order('created_at', { ascending: false });
      docsQuery = docsQuery.order('created_at', { ascending: false });
      socialQuery = socialQuery.order('created_at', { ascending: false });
      websiteQuery = websiteQuery.order('created_at', { ascending: false });

      const [tablesResponse, docsResponse, socialResponse, websiteResponse] = await Promise.all([
        tablesQuery,
        docsQuery,
        socialQuery,
        websiteQuery
      ]);

      if (tablesResponse.error) throw tablesResponse.error;
      if (docsResponse.error) throw docsResponse.error;
      if (socialResponse.error) throw socialResponse.error;
      if (websiteResponse.error) throw websiteResponse.error;

      // Transform social content to match document structure
      const socialContent = (socialResponse.data || []).map(item => ({
        ...item,
        name: item.title,
        isSocial: true
      }));

      // Transform website SEO checks to match document structure
      const websiteContent = (websiteResponse.data || []).map(item => ({
        ...item,
        name: item.title,
        isWebsite: true
      }));

      setTables(tablesResponse.data || []);
      setDocuments([
        ...docsResponse.data || [],
        ...socialContent,
        ...websiteContent
      ]);
    } catch (error) {
      console.error('Error loading content:', error);
      addToast('Error loading content', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadFolders = async () => {
    try {
      let query = supabase
        .from('data_folders')
        .select('*')
        .eq('project_id', currentProject.id);

      // If we're in a folder, only get its direct children
      if (currentFolder) {
        query = query.eq('parent_id', currentFolder.id);
      } else {
        // If we're at root, get only top-level folders
        query = query.is('parent_id', null);
      }

      const { data: foldersData, error } = await query;

      if (error) throw error;
      setFolders(foldersData || []);
    } catch (error) {
      console.error('Error loading folders:', error);
      addToast('Error loading folders', 'error');
    }
  };

  const handleTableCreated = (newTable) => {
    setTables([newTable, ...tables]);
  };

  const handleCreateDocument = async () => {
    try {
      const { data, error } = await supabase
        .from('data_docs')
        .insert({
          project_id: currentProject.id,
          name: 'Untitled Document',
          content: ''
        })
        .select()
        .single();

      if (error) throw error;
      navigate(`/content/documents/${data.id}`);
    } catch (error) {
      console.error('Error creating document:', error);
      addToast('Failed to create document', 'error');
    }
  };

  const handleCreateTable = async () => {
    try {
      const { data, error } = await supabase
        .from('data_tables')
        .insert({
          project_id: currentProject.id,
          name: 'Untitled Table',
          dataset: [
            { A: '', B: '', C: '', D: '' }  // First empty row with default columns
          ],
          column_order: ['A', 'B', 'C', 'D'],    // Define default columns
          folder_id: currentFolder?.id || null,
          created_by: user.id,
          updated_by: user.id
        })
        .select()
        .single();

      if (error) throw error;
      navigate(`/content/tables/${data.id}`);
    } catch (error) {
      console.error('Error creating table:', error);
      addToast('Failed to create table', 'error');
    }
  };

  const handleCreateWebsite = async () => {
    try {
      const { data, error } = await supabase
        .from('website_seo_checks')
        .insert({
          project_id: currentProject.id,
          title: 'New Website Analysis',
          created_by: user.id,
          updated_by: user.id,
          folder_id: currentFolder?.id || null
        })
        .select()
        .single();

      if (error) throw error;
      navigate(`/content/website/${data.id}`);
    } catch (error) {
      console.error('Error creating website analysis:', error);
      addToast('Failed to create website analysis', 'error');
    }
  };

  const handleCreateSocial = async () => {
    try {
      const { data, error } = await supabase
        .from('content')
        .insert({
          project_id: currentProject.id,
          title: 'Untitled Social Post',
          content: '',
          node_type: '',
          created_by: user.id,
          updated_by: user.id,
          folder_id: currentFolder?.id || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      navigate(`/content/social/${data.id}`);
    } catch (error) {
      console.error('Error creating social post:', error);
      addToast('Failed to create social post', 'error');
    }
  };

  const handleCreateBlog = async () => {
    try {
      const { data, error } = await supabase
        .from('content')
        .insert({
          project_id: currentProject.id,
          title: 'Untitled Blog Post',
          content: '',
          node_type: 'blog',
          created_by: user.id,
          updated_by: user.id,
          folder_id: currentFolder?.id || null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      navigate(`/content/blog/${data.id}`);
    } catch (error) {
      console.error('Error creating blog post:', error);
      addToast('Failed to create blog post', 'error');
    }
  };

  const handleDocumentTypeSelect = (type) => {
    setShowDocumentTypeModal(false);
    if (type === 'folder') {
      setShowCreateFolderModal(true);
    } else if (type === 'table') {
      handleCreateTable();
    } else if (type === 'social') {
      handleCreateSocial();
    } else if (type === 'blog') {
      handleCreateBlog();
    } else if (type === 'website') {
      handleCreateWebsite();
    } else {
      handleCreateDocument();
    }
  };

  const handleDelete = async (e, item) => {
    e.stopPropagation();

    const confirmed = await confirm({
      title: 'Delete Document',
      message: `Are you sure you want to delete "${item.name}"? This action cannot be undone.`,
      confirmText: 'Delete',
      confirmVariant: 'danger'
    });

    if (confirmed) {
      try {
        if (item.dataset) {
          const { error } = await supabase
            .from('data_tables')
            .delete()
            .eq('id', item.id);

          if (error) throw error;
          setTables(tables.filter(t => t.id !== item.id));
        } else {
          const { error } = await supabase
            .from('data_docs')
            .delete()
            .eq('id', item.id);

          if (error) throw error;
          setDocuments(documents.filter(d => d.id !== item.id));
        }

        addToast('Document deleted successfully', 'success');
      } catch (error) {
        console.error('Error deleting document:', error);
        addToast('Failed to delete document', 'error');
      }
    }
  };

  const handleCreateFolder = async (name) => {
    try {
      const { data: folder, error } = await supabase
        .from('data_folders')
        .insert([{
          name,
          project_id: currentProject.id,
          parent_id: currentFolder?.id || null
        }])
        .select()
        .single();

      if (error) throw error;

      setFolders(prev => [...prev, folder]);
      addToast('Folder created successfully', 'success');
      setShowCreateFolderModal(false);
    } catch (error) {
      console.error('Error creating folder:', error);
      addToast('Error creating folder', 'error');
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { draggableId, destination } = result;
    
    // Use regex to extract type and id to handle all formats
    const match = draggableId.match(/(website|table|doc)-([0-9a-f-]+)/);
    if (!match) {
      console.error('Invalid draggable ID format:', draggableId);
      return;
    }

    const [, type, id] = match;
    
    try {
      let tableName;
      switch (type) {
        case 'table':
          tableName = 'data_tables';
          break;
        case 'doc':
          tableName = 'data_docs';
          break;
        case 'website':
          tableName = 'website_seo_checks';
          break;
        default:
          console.error('Unknown type:', type);
          return;
      }

      const targetFolderId = destination.droppableId === 'root' 
        ? null 
        : destination.droppableId.replace('folder-', '');

      console.log('Moving item:', { type, id, tableName, targetFolderId });

      const { error } = await supabase
        .from(tableName)
        .update({ 
          folder_id: targetFolderId,
          updated_at: new Date().toISOString(),
          updated_by: user.id
        })
        .eq('id', id);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Refresh content after successful move
      await loadContent();
      addToast('Item moved successfully', 'success');
      
    } catch (error) {
      console.error('Error moving item:', error);
      addToast('Failed to move item', 'error');
    }
  };

  const handleDeleteFolder = async (e, folder) => {
    e.stopPropagation();

    try {
      // Check if folder has children
      const { data: childFolders, error: childError } = await supabase
        .from('data_folders')
        .select('id')
        .eq('parent_id', folder.id);

      if (childError) throw childError;

      if (childFolders?.length > 0) {
        addToast('Cannot delete folder that contains other folders', 'error');
        return;
      }

      // Check if folder has documents or tables
      const folderContents = [...tables, ...documents].filter(item => item.folder_id === folder.id);
      if (folderContents.length > 0) {
        addToast('Cannot delete folder that contains items', 'error');
        return;
      }

      const confirmed = await confirm({
        title: 'Delete Folder',
        message: `Are you sure you want to delete "${folder.name}"? This action cannot be undone.`,
        confirmText: 'Delete',
        confirmVariant: 'danger'
      });

      if (confirmed) {
        const { error } = await supabase
          .from('data_folders')
          .delete()
          .eq('id', folder.id);

        if (error) throw error;

        setFolders(folders.filter(f => f.id !== folder.id));
        if (currentFolder?.id === folder.id) {
          // Navigate to parent if exists, otherwise to root
          if (folder.parent_id) {
            const { data: parentFolder } = await supabase
              .from('data_folders')
              .select('*')
              .eq('id', folder.parent_id)
              .single();
            
            setCurrentFolder(parentFolder);
            setBreadcrumbs(prev => prev.slice(0, -1));
          } else {
            setCurrentFolder(null);
            setBreadcrumbs([]);
          }
        }
        addToast('Folder deleted successfully', 'success');
      }
    } catch (error) {
      console.error('Error deleting folder:', error);
      addToast('Failed to delete folder', 'error');
    }
  };

  const sortItems = (items) => {
    return items.sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      }
      return new Date(b.updated_at) - new Date(a.updated_at);
    });
  };

  const navigateToFolder = async (folder) => {
    try {
      // Get the full path to this folder for breadcrumbs
      const path = [];
      let currentId = folder.id;
      
      while (currentId) {
        const { data: folderData, error } = await supabase
          .from('data_folders')
          .select('id, name, parent_id')
          .eq('id', currentId)
          .single();
        
        if (error) throw error;
        
        path.unshift(folderData);
        currentId = folderData.parent_id;
      }
      
      setBreadcrumbs(path);
      setCurrentFolder(folder);
    } catch (error) {
      console.error('Error navigating to folder:', error);
      addToast('Error navigating to folder', 'error');
    }
  };


  const handleMove = async (targetFolderId) => {
    if (!selectedItem) return;
    
    try {
      let tableName;
      switch (selectedItem.type) {
        case 'table':
          tableName = 'data_tables';
          break;
        case 'doc':
          tableName = 'data_docs';
          break;
        case 'website':
          tableName = 'website_seo_checks';
          break;
        default:
          throw new Error('Invalid item type');
      }

      const { error } = await supabase
        .from(tableName)
        .update({ 
          folder_id: targetFolderId,
          updated_at: new Date().toISOString(),
          updated_by: user.id
        })
        .eq('id', selectedItem.id);

      if (error) throw error;
      
      // Refresh content
      await loadContent();
      setShowMoveModal(false);
      addToast('Item moved successfully', 'success');
    } catch (error) {
      console.error('Error moving item:', error);
      addToast('Failed to move item', 'error');
    }
  };

  const getItemType = (item) => {
    if (item.dataset) return 'table';
    if (item.isWebsite) return 'website';
    if (item.isSocial) return 'social';
    return 'doc';
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex-1 bg-dark-100 flex flex-col">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-semibold flex items-center gap-2">
                <button
                  onClick={() => {
                    setCurrentFolder(null);
                    setBreadcrumbs([]);
                  }}
                  className="hover:text-accent-primary transition-colors"
                >
                  Documents
                </button>

                {currentFolder && (
                  <>
                    <span className="text-gray-500">/</span>
                    {breadcrumbs.map((folder, index) => (
                      <React.Fragment key={folder.id}>
                        {index > 0 && <span className="text-gray-500">/</span>}
                        <button
                          onClick={() => {
                            setCurrentFolder(folder);
                            setBreadcrumbs(prev => prev.slice(0, index + 1));
                          }}
                          className={`hover:text-accent-primary transition-colors ${
                            index === breadcrumbs.length - 1 ? 'text-white' : 'text-gray-400'
                          }`}
                        >
                          {folder.name}
                        </button>
                      </React.Fragment>
                    ))}
                  </>
                )}
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Sort Dropdown */}
              <div className="flex items-center gap-2">
                <ArrowsUpDownIcon className="w-5 h-5 text-gray-400" />
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-dark-300 border border-divider rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent-primary"
                >
                  <option value="updated">Last Updated</option>
                  <option value="name">Name</option>
                </select>
              </div>

              {/* View Toggle */}
              <div className="flex items-center bg-dark-300 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'grid' 
                      ? 'bg-dark-200 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                  title="Grid view"
                >
                  <ViewColumnsIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-md transition-colors ${
                    viewMode === 'list' 
                      ? 'bg-dark-200 text-white' 
                      : 'text-gray-400 hover:text-white'
                  }`}
                  title="List view"
                >
                  <ListBulletIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Create Button */}
              <button
                onClick={() => setShowDocumentTypeModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent-primary text-white rounded-lg hover:bg-accent-primary/90 transition-colors"
              >
                <PlusIcon className="w-5 h-5" />
                <span>Create</span>
              </button>
            </div>
          </div>
        </div>

        <div className="flex-1">
          <div className="p-4 min-h-full">
            <div className={`${
              viewMode === 'grid'
                ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4'
                : 'flex flex-col space-y-2'
            }`}>
              {folders.length > 0 && (
                <div className={`${viewMode === 'grid' ? 'col-span-full' : ''} mb-2`}>
                  <span className="text-xs font-medium tracking-wider text-gray-400 uppercase">
                    Folders
                  </span>
                </div>
              )}

              {/* Folders Section - Each folder is a droppable target */}
              {folders.map((folder) => (
                <StrictModeDroppable key={folder.id} droppableId={`folder-${folder.id}`}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      onClick={() => navigateToFolder(folder)}
                      className={`${
                        viewMode === 'grid'
                          ? 'bg-dark-200 rounded-lg p-4 hover:bg-dark-300'
                          : 'bg-dark-200 rounded-lg p-3 hover:bg-dark-300 flex items-center'
                      } transition-colors cursor-pointer group ${
                        snapshot.isDraggingOver ? 'ring-2 ring-accent-primary bg-dark-400' : ''
                      }`}
                    >
                      {viewMode === 'grid' ? (
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-2">
                            <FolderIcon className="w-5 h-5 text-gray-400" />
                            <h3 className="font-medium">{folder.name}</h3>
                          </div>
                          <div className="relative">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuOpen(folder.id);
                              }}
                              className="text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                            >
                              <EllipsisVerticalIcon className="w-5 h-5" />
                            </button>
                            
                            {menuOpen === folder.id && (
                              <div 
                                className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-dark-200 ring-1 ring-black ring-opacity-5 z-50"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="py-1">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteFolder(e, folder);
                                      setMenuOpen(null);
                                    }}
                                    className="block w-full px-4 py-2 text-sm text-red-400 hover:bg-dark-300 text-left"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center gap-3 flex-1">
                            <FolderIcon className="w-5 h-5 text-gray-400 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <h3 className="text-lg font-medium truncate">{folder.name}</h3>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-400">
                              {formatDateTime(folder.updated_at)}
                            </span>
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMenuOpen(folder.id);
                                }}
                                className="text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <EllipsisVerticalIcon className="w-5 h-5" />
                              </button>
                              
                              {menuOpen === folder.id && (
                                <div 
                                  className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-dark-200 ring-1 ring-black ring-opacity-5 z-50"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <div className="py-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteFolder(e, folder);
                                        setMenuOpen(null);
                                      }}
                                      className="block w-full px-4 py-2 text-sm text-red-400 hover:bg-dark-300 text-left"
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </StrictModeDroppable>
              ))}

              {(currentFolder ? 
                [...tables, ...documents].filter(item => item.folder_id === currentFolder.id).length > 0 :
                [...tables, ...documents].filter(item => item.folder_id === null).length > 0
              ) && (
                <>
                  <div className={`${viewMode === 'grid' ? 'col-span-full' : ''} mb-2 mt-4 space-y-18`} style={{ marginTop: 20}}>
                    <span className="text-xs font-medium tracking-wider text-gray-400 uppercase">
                      Files
                    </span>
                  </div>

                  <div className={`${viewMode === 'grid' ? 'col-span-full' : ''}`}>
                    <StrictModeDroppable droppableId="root">
                      {(provided) => (
                        <div 
                          ref={provided.innerRef} 
                          {...provided.droppableProps}
                          className={viewMode === 'grid' ? 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4' : ''}
                        >
                          {currentFolder ? (
                            [...tables, ...documents]
                              .filter(item => item.folder_id === currentFolder.id)
                              .sort((a, b) => sortItems([a, b])[0] === a ? -1 : 1)
                              .map((item, index) => (
                                <Draggable
                                  key={`${getItemType(item)}-${item.id}`}
                                  draggableId={`${getItemType(item)}-${item.id}`}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`${
                                        viewMode === 'grid'
                                          ? 'bg-dark-200 rounded-lg p-4 hover:bg-dark-300'
                                          : 'bg-dark-200 rounded-lg p-3 hover:bg-dark-300 flex items-center'
                                      } transition-colors cursor-pointer group ${
                                        snapshot.isDragging ? 'shadow-lg ring-2 ring-accent-primary' : ''
                                      }`}
                                      onClick={() => navigate(
                                        item.node_type === 'blog'
                                          ? `/content/blog/${item.id}`
                                          : item.isSocial 
                                          ? `/content/social/${item.id}`
                                          : item.isWebsite
                                          ? `/content/website/${item.id}`
                                          : `/content/${item.dataset ? 'tables' : 'documents'}/${item.id}`
                                      )}
                                    >
                                      {viewMode === 'grid' ? (
                                        <div>
                                          <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                              {item.node_type === 'blog' ? (
                                                <NewspaperIcon className="w-5 h-5 text-accent-primary" />
                                              ) : item.isSocial ? (
                                                <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-accent-primary" />
                                              ) : item.isWebsite ? (
                                                <GlobeAltIcon className="w-5 h-5 text-accent-primary" />
                                              ) : item.dataset ? (
                                                <TableCellsIcon className="w-5 h-5 text-accent-primary" />
                                              ) : (
                                                <DocumentTextIcon className="w-5 h-5 text-accent-primary" />
                                              )}
                                              <h3 className="font-medium truncate">{item.name}</h3>
                                            </div>
                                            <div className="relative ml-2 flex-shrink-0">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setMenuOpen(item.id);
                                                }}
                                                className="text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100 p-1 hover:bg-dark-400 rounded"
                                              >
                                                <EllipsisVerticalIcon className="w-5 h-5" />
                                              </button>
                                              
                                              {menuOpen === item.id && (
                                                <div 
                                                  className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-dark-200 ring-1 ring-black ring-opacity-5 z-50"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <div className="py-1">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedItem({
                                                          type: item.isWebsite ? 'website' : item.dataset ? 'table' : 'doc',
                                                          id: item.id
                                                        });
                                                        setShowMoveModal(true);
                                                        setMenuOpen(null);
                                                      }}
                                                      className="block w-full px-4 py-2 text-sm text-gray-300 hover:bg-dark-300 text-left flex items-center gap-2"
                                                    >
                                                      <FolderIcon className="w-4 h-4" />
                                                      Move to folder
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(e, item);
                                                        setMenuOpen(null);
                                                      }}
                                                      className="block w-full px-4 py-2 text-sm text-red-400 hover:bg-dark-300 text-left flex items-center gap-2"
                                                    >
                                                      <TrashIcon className="w-4 h-4" />
                                                      Delete
                                                    </button>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          <div className="mt-auto">
                                            <div className="flex items-center gap-2 text-sm text-gray-400">
                                              {item.dataset ? (
                                                <span>{`${item.dataset.filter(row => 
                                                  Object.values(row).some(value => value !== '')
                                                ).length} rows`}</span>
                                              ) : item.isWebsite ? (
                                                <span>SEO Analysis</span>
                                              ) : item.isSocial ? (
                                                <span>Social Post</span>
                                              ) : (
                                                <span>Text Document</span>
                                              )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                              {formatDateTime(item.updated_at)}
                                            </p>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <div className="flex items-center gap-3 flex-1">
                                            {item.node_type === 'blog' ? (
                                              <NewspaperIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            ) : item.isSocial ? (
                                              <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            ) : item.isWebsite ? (
                                              <GlobeAltIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            ) : item.dataset ? (
                                              <TableCellsIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            ) : (
                                              <DocumentTextIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                              <h3 className="font-medium truncate">{item.name}</h3>
                                              <p className="text-sm text-gray-400">
                                                {item.dataset ? (
                                                  `${item.dataset.filter(row => 
                                                    Object.values(row).some(value => value !== '')
                                                  ).length} rows`
                                                ) : (
                                                  'Text Document'
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-4">
                                            <span className="text-sm text-gray-400">
                                              {formatDateTime(item.updated_at)}
                                            </span>
                                            <div className="relative">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setMenuOpen(item.id);
                                                }}
                                                className="text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                                              >
                                                <EllipsisVerticalIcon className="w-5 h-5" />
                                              </button>
                                              
                                              {menuOpen === item.id && (
                                                <div 
                                                  className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-dark-200 ring-1 ring-black ring-opacity-5 z-50"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <div className="py-1">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedItem({
                                                          type: item.isWebsite ? 'website' : item.dataset ? 'table' : 'doc',
                                                          id: item.id
                                                        });
                                                        setShowMoveModal(true);
                                                        setMenuOpen(null);
                                                      }}
                                                      className="block w-full px-4 py-2 text-sm text-gray-300 hover:bg-dark-300 text-left"
                                                    >
                                                      Move to folder
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(e, item);
                                                        setMenuOpen(null);
                                                      }}
                                                      className="block w-full px-4 py-2 text-sm text-red-400 hover:bg-dark-300 text-left"
                                                    >
                                                      Delete
                                                    </button>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              ))
                          ) : (
                            [...tables, ...documents]
                              .filter(item => item.folder_id === null)
                              .sort((a, b) => sortItems([a, b])[0] === a ? -1 : 1)
                              .map((item, index) => (
                                <Draggable
                                  key={`${getItemType(item)}-${item.id}`}
                                  draggableId={`${getItemType(item)}-${item.id}`}
                                  index={index}
                                >
                                  {(provided, snapshot) => (
                                    <div
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                      className={`${
                                        viewMode === 'grid'
                                          ? 'bg-dark-200 rounded-lg p-4 hover:bg-dark-300'
                                          : 'bg-dark-200 rounded-lg p-3 hover:bg-dark-300 flex items-center'
                                      } transition-colors cursor-pointer group ${
                                        snapshot.isDragging ? 'shadow-lg ring-2 ring-accent-primary' : ''
                                      }`}
                                      onClick={() => navigate(
                                        item.node_type === 'blog'
                                          ? `/content/blog/${item.id}`
                                          : item.isSocial 
                                          ? `/content/social/${item.id}`
                                          : item.isWebsite
                                          ? `/content/website/${item.id}`
                                          : `/content/${item.dataset ? 'tables' : 'documents'}/${item.id}`
                                      )}
                                    >
                                      {viewMode === 'grid' ? (
                                        <div>
                                          <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                              {item.node_type === 'blog' ? (
                                                <NewspaperIcon className="w-5 h-5 text-accent-primary" />
                                              ) : item.isSocial ? (
                                                <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-accent-primary" />
                                              ) : item.isWebsite ? (
                                                <GlobeAltIcon className="w-5 h-5 text-accent-primary" />
                                              ) : item.dataset ? (
                                                <TableCellsIcon className="w-5 h-5 text-accent-primary" />
                                              ) : (
                                                <DocumentTextIcon className="w-5 h-5 text-accent-primary" />
                                              )}
                                              <h3 className="font-medium truncate">{item.name}</h3>
                                            </div>
                                            <div className="relative ml-2 flex-shrink-0">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setMenuOpen(item.id);
                                                }}
                                                className="text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100 p-1 hover:bg-dark-400 rounded"
                                              >
                                                <EllipsisVerticalIcon className="w-5 h-5" />
                                              </button>
                                              
                                              {menuOpen === item.id && (
                                                <div 
                                                  className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-dark-200 ring-1 ring-black ring-opacity-5 z-50"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <div className="py-1">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedItem({
                                                          type: item.isWebsite ? 'website' : item.dataset ? 'table' : 'doc',
                                                          id: item.id
                                                        });
                                                        setShowMoveModal(true);
                                                        setMenuOpen(null);
                                                      }}
                                                      className="block w-full px-4 py-2 text-sm text-gray-300 hover:bg-dark-300 text-left flex items-center gap-2"
                                                    >
                                                      <FolderIcon className="w-4 h-4" />
                                                      Move to folder
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(e, item);
                                                        setMenuOpen(null);
                                                      }}
                                                      className="block w-full px-4 py-2 text-sm text-red-400 hover:bg-dark-300 text-left flex items-center gap-2"
                                                    >
                                                      <TrashIcon className="w-4 h-4" />
                                                      Delete
                                                    </button>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                          <div className="mt-auto">
                                            <div className="flex items-center gap-2 text-sm text-gray-400">
                                              {item.dataset ? (
                                                <span>{`${item.dataset.filter(row => 
                                                  Object.values(row).some(value => value !== '')
                                                ).length} rows`}</span>
                                              ) : item.isWebsite ? (
                                                <span>SEO Analysis</span>
                                              ) : item.isSocial ? (
                                                <span>Social Post</span>
                                              ) : (
                                                <span>Text Document</span>
                                              )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1">
                                              {formatDateTime(item.updated_at)}
                                            </p>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <div className="flex items-center gap-3 flex-1">
                                            {item.node_type === 'blog' ? (
                                              <NewspaperIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            ) : item.isSocial ? (
                                              <ChatBubbleBottomCenterTextIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            ) : item.isWebsite ? (
                                              <GlobeAltIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            ) : item.dataset ? (
                                              <TableCellsIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            ) : (
                                              <DocumentTextIcon className="w-5 h-5 text-accent-primary flex-shrink-0" />
                                            )}
                                            <div className="min-w-0 flex-1">
                                              <h3 className="font-medium truncate">{item.name}</h3>
                                              <p className="text-sm text-gray-400">
                                                {item.dataset ? (
                                                  `${item.dataset.filter(row => 
                                                    Object.values(row).some(value => value !== '')
                                                  ).length} rows`
                                                ) : (
                                                  'Text Document'
                                                )}
                                              </p>
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-4">
                                            <span className="text-sm text-gray-400">
                                              {formatDateTime(item.updated_at)}
                                            </span>
                                            <div className="relative">
                                              <button
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  setMenuOpen(item.id);
                                                }}
                                                className="text-gray-400 hover:text-white transition-colors opacity-0 group-hover:opacity-100"
                                              >
                                                <EllipsisVerticalIcon className="w-5 h-5" />
                                              </button>
                                              
                                              {menuOpen === item.id && (
                                                <div 
                                                  className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-dark-200 ring-1 ring-black ring-opacity-5 z-50"
                                                  onClick={(e) => e.stopPropagation()}
                                                >
                                                  <div className="py-1">
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedItem({
                                                          type: item.isWebsite ? 'website' : item.dataset ? 'table' : 'doc',
                                                          id: item.id
                                                        });
                                                        setShowMoveModal(true);
                                                        setMenuOpen(null);
                                                      }}
                                                      className="block w-full px-4 py-2 text-sm text-gray-300 hover:bg-dark-300 text-left"
                                                    >
                                                      Move to folder
                                                    </button>
                                                    <button
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleDelete(e, item);
                                                        setMenuOpen(null);
                                                      }}
                                                      className="block w-full px-4 py-2 text-sm text-red-400 hover:bg-dark-300 text-left"
                                                    >
                                                      Delete
                                                    </button>
                                                  </div>
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </Draggable>
                              ))
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </StrictModeDroppable>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        <CreateDocumentModal
          isOpen={showDocumentTypeModal}
          onClose={() => setShowDocumentTypeModal(false)}
          onSelectType={handleDocumentTypeSelect}
        />

        <CreateTableModal
          isOpen={showCreateTableModal}
          onClose={() => setShowCreateTableModal(false)}
          projectId={currentProject?.id}
          onTableCreated={handleTableCreated}
        />

        <CreateFolderModal
          isOpen={showCreateFolderModal}
          onClose={() => setShowCreateFolderModal(false)}
          onSubmit={handleCreateFolder}
        />

        <MoveFolderModal
          isOpen={showMoveModal}
          onClose={() => {
            setShowMoveModal(false);
            setSelectedItem(null);
          }}
          onMove={handleMove}
          currentProjectId={currentProject?.id}
        />
      </div>
    </DragDropContext>
  );
}

export default Documents; 